import * as React from "react";
import type {FixedBreak} from "@/shared/timr";
import {isValidTimeInputValue, TimeInput} from "@/shared/ui/primitives/TimeInput";
import {
	FIXED_BREAK_FIELDS,
	hasFixedBreakRangeError,
	type FixedBreakField,
	type FixedBreakKey
} from "@/features/admin/breakModel/fixedBreakConfigurationModel";

interface FixedBreakAllDaysEqualConfigurationProps {
	/** Configuration object key whose ranges should be edited, either the shared fixed break or a weekday-specific fixed break. */
	fixedBreakKey: FixedBreakKey;

	/** The current from/to values for the three fixed-break ranges displayed in this block. */
	fixedBreak: FixedBreak;

	/** Fully qualified backend field names that should be highlighted as invalid, e.g. fixedBreak.from1. */
	errorFields: Set<string>;

	/** Persists one edited range endpoint back into the owning break-model configuration. */
	onChange: (fixedBreakKey: FixedBreakKey, field: FixedBreakField, value: string | null) => void;

	/** Clears both endpoints when the user disables a fixed-break row. */
	onClearRange: (fixedBreakKey: FixedBreakKey, fromField: FixedBreakField, toField: FixedBreakField) => void;
}

/**
 * Renders the all-days-equal fixed-break editor as three toggleable time ranges.
 *
 * The backend model stores fixed breaks as numbered `fromN` / `toN` pairs, while the
 * dialog presents each pair as a single editable row with shared validation highlighting.
 */
export function FixedBreakAllDaysEqualConfiguration({fixedBreakKey, fixedBreak, errorFields, onChange, onClearRange}: Readonly<FixedBreakAllDaysEqualConfigurationProps>) {
	return (
		<div className="fixed-break-equal-rows">
			{/* The product supports exactly three fixed breaks, mapped from from1/to1 through from3/to3. */}
			{FIXED_BREAK_FIELDS.map(([fromField, toField], index) => (
				<FixedBreakAllDaysEqualTimeRange
					key={`${fromField}-${toField}`}
					fixedBreakKey={fixedBreakKey}
					index={index}
					fromField={fromField}
					toField={toField}
					fixedBreak={fixedBreak}
					errorFields={errorFields}
					onChange={onChange}
					onClearRange={onClearRange}
				/>
			))}
		</div>
	);
}

interface FixedBreakAllDaysEqualTimeRangeProps {
	/** Configuration object key whose range endpoint should be read and updated. */
	fixedBreakKey: FixedBreakKey;

	/** Zero-based row index used for labels and stable checkbox IDs. */
	index: number;

	/** Backend field name for the range start value. */
	fromField: FixedBreakField;

	/** Backend field name for the range end value. */
	toField: FixedBreakField;

	/** Current fixed-break values for the selected shared or weekday configuration. */
	fixedBreak: FixedBreak;

	/** Fully qualified backend field names that should be highlighted as invalid. */
	errorFields: Set<string>;

	/** Persists one edited endpoint back into the parent configuration. */
	onChange: (fixedBreakKey: FixedBreakKey, field: FixedBreakField, value: string | null) => void;

	/** Clears both endpoints when the user disables the fixed-break row. */
	onClearRange: (fixedBreakKey: FixedBreakKey, fromField: FixedBreakField, toField: FixedBreakField) => void;
}

/**
 * Renders one all-days-equal fixed-break range with an enable checkbox and two time inputs.
 *
 * Disabling the checkbox clears both endpoints because the legacy backend treats an empty
 * `fromN` / `toN` pair as an unused fixed-break slot.
 */
function FixedBreakAllDaysEqualTimeRange(props: Readonly<FixedBreakAllDaysEqualTimeRangeProps>) {
	const checked = isValidTimeInputValue(props.fixedBreak[props.fromField]) || isValidTimeInputValue(props.fixedBreak[props.toField]);

	const toggleEnabled = (checked: boolean) => {
		if (!checked) {
			props.onClearRange(props.fixedBreakKey, props.fromField, props.toField);
		}
	};
	const hasError = hasFixedBreakRangeError(props.errorFields, props.fixedBreakKey, props.fromField, props.toField);

	return (
		<div className="fixed-break-row">
			<div className={`input-group fixed-break-equal-time-range${hasError ? " has-error" : ""}`}>
				<span className="input-group-addon">
					<input
						type="checkbox"
						checked={checked}
						aria-label={messages.withArguments("breakModel.fixedBreak.breakNumber", props.index + 1)}
						onChange={event => toggleEnabled(event.target.checked)}
					/>
				</span>
				<span className="input-group-addon fixed-break-equal-time-label">
					{messages["breakModel.automaticBreaks.fixedBreakFrom"]}
				</span>
				<TimeInput
					name={`${(props.fixedBreakKey)}.${(props.fromField)}`}
					value={props.fixedBreak[props.fromField]}
					ariaLabel={`${(messages.withArguments("breakModel.fixedBreak.breakNumber", props.index + 1))} ${messages["breakModel.automaticBreaks.fixedBreakFrom"]}`}
					className="fixed-break-time-input"
					onValueChange={value => props.onChange(props.fixedBreakKey, props.fromField, value)}
				/>
				<span className="input-group-addon">{messages["breakModel.automaticBreaks.fixedBreakTo"]}</span>
				<TimeInput
					name={`${(props.fixedBreakKey)}.${(props.toField)}`}
					value={props.fixedBreak[props.toField]}
					ariaLabel={`${(messages.withArguments("breakModel.fixedBreak.breakNumber", props.index + 1))} ${messages["breakModel.automaticBreaks.fixedBreakTo"]}`}
					className="fixed-break-time-input"
					onValueChange={value => props.onChange(props.fixedBreakKey, props.toField, value)}
				/>
				<span className="input-group-addon">{messages["breakModel.automaticBreaks.fixedBreakOClock"]}</span>
			</div>
		</div>
	);
}
